import './TimeFrameSelector.scss';
import React, { useState, useMemo, useEffect } from 'react';
import { coerceEpoch, epochToDateTimeString } from 'helpers/common.helper';
import { DATE_FORMAT_OPTIONS } from 'pages/Analytics/Overview/constants';
import { FlightSelect } from '@flybits/design-system';
import DateRangePicker from 'components/Shared/DateRangePicker';
import type { Preset } from 'components/Shared/DateRangePicker';
import { getPresetRange } from 'components/Shared/DateRangePicker/DateRangePicker.utils';
import { AdvancedDateRange, ChartConfigState, INTERVAL_OPTIONS } from 'pages/Analytics/types';
import { TIMEFRAME_INTERVAL_OPTIONS } from 'pages/Analytics/CustomAnalytics/CreateOrEditChart/constants';
import { MAX_DAYS_FOR_HOURLY_INTERVAL, getRelativeTimeFrameDayCount } from 'pages/Analytics/helpers';

const DATERANGE_PRESETS: Preset[] = ['today', 'last7', 'last30', 'last60', 'last90', 'custom'];

/** Map relative day-offsets to DateRangePicker presets (Today is API offset 1 → picker 0). */
const PRESET_BY_DAY_OFFSET: Record<number, Preset> = {
  0: 'today',
  7: 'last7',
  30: 'last30',
  60: 'last60',
  90: 'last90',
};

const MAIN_CLASS = 'timeframe-selector';
const CLASSES = {
  LABEL: `${MAIN_CLASS}__label`,
  BLOCK: `${MAIN_CLASS}__block`,
  PREVIEW: `${MAIN_CLASS}__preview`,
  DATE_RANGE: `${MAIN_CLASS}__date-range`,
  DATE_RANGE_DD: `${MAIN_CLASS}__date-range__dropdown`,
};

interface TimeFrameSelectorProps {
  chartConfig: ChartConfigState;
  onChange: (timeFrame: ChartConfigState['timeFrame']) => void;
  onValidation: (isValid: boolean) => void;
  hiddenIntervals?: INTERVAL_OPTIONS[];
}

export default function TimeFrameSelector({
  chartConfig,
  onChange,
  onValidation,
  hiddenIntervals,
}: TimeFrameSelectorProps) {
  const { start, end, period, relative } = chartConfig.timeFrame || {};
  const hasParentRelative = relative?.[0] !== undefined && relative?.[0] !== null && !!relative?.[1];
  const isTimePeriodValid = hasParentRelative || (!!start && !!end);

  // Controlled value derived from parent chartConfig — single source of truth.
  // Avoids local selectedDateRange + sync effect, which could briefly pair a Today
  // pick with a still-Last-7 parent and then overwrite the pick back to Last 7.
  const selectedDateRange = useMemo<AdvancedDateRange>(() => {
    // Prefer relative metadata when present (same as serialize / step labels). Preset picks
    // populate BOTH start/end and relative; reconstruct from the preset so stale absolute
    // dates from a previous pick can't fight the relative offset in detectPreset.
    if (relative && relative[0] !== undefined && relative[0] >= 0 && !!relative[1]) {
      // 'Today' is serialized with relativeTimeOffset 1, but the picker expects 0
      const isDaily = relative[1] === INTERVAL_OPTIONS.DAILY || relative[1] === 'day';
      const offset = isDaily && relative[0] === 1 ? 0 : relative[0];
      if (isDaily) {
        const preset = PRESET_BY_DAY_OFFSET[offset];
        if (preset) {
          return getPresetRange(preset);
        }
      }
      if (start && end) {
        return [start, end, offset, relative[1]];
      }
      return [null, null, offset, relative[1]];
    }
    if (start && end) {
      return [start, end];
    }
    return getPresetRange('last7');
  }, [start, end, relative]);

  const intervalOptions = useMemo(
    () =>
      TIMEFRAME_INTERVAL_OPTIONS.map((interval) => {
        const { label, value, duration } = interval;
        return { key: value, name: label, duration };
      }).filter((opt) => {
        if (opt.key && hiddenIntervals?.includes(opt.key as INTERVAL_OPTIONS)) {
          return false;
        }
        const dateRange = selectedDateRange;
        const [rangeStart, rangeEnd, relativeTimeOffset, relativeTimeUnit] = dateRange;

        // Prefer relative metadata when present. Preset picks (Last 7/30/…) include both
        // absolute start/end AND relative [offset, unit]; edit hydration only restores
        // relative. Checking absolute first made create show Weekly for Last 7 (span is
        // slightly > 7 days wall-clock) while edit hid it (7 * day === week duration).
        const hasRelative = relativeTimeOffset !== null && relativeTimeOffset !== undefined && !!relativeTimeUnit;

        // Hourly is only valid for ranges of MAX_DAYS_FOR_HOURLY_INTERVAL days or fewer —
        // matching the analytics moduleconfig API ("time interval hour is too small…").
        if (opt.key === INTERVAL_OPTIONS.HOURLY) {
          if (hasRelative) {
            if (relativeTimeOffset === 0) return true;
            if (getRelativeTimeFrameDayCount(relativeTimeOffset, relativeTimeUnit) > MAX_DAYS_FOR_HOURLY_INTERVAL) {
              return false;
            }
          } else if (rangeStart && rangeEnd) {
            const durationSeconds = coerceEpoch(rangeEnd) - coerceEpoch(rangeStart);
            const dayCount = durationSeconds / (24 * 60 * 60);
            if (dayCount > MAX_DAYS_FOR_HOURLY_INTERVAL) return false;
          }
        }

        if (hasRelative) {
          if (relativeTimeOffset === 0) {
            return opt.key === INTERVAL_OPTIONS.HOURLY;
          }
          const intervalOption =
            TIMEFRAME_INTERVAL_OPTIONS.find((opt) => opt.value === relativeTimeUnit) || TIMEFRAME_INTERVAL_OPTIONS[0];
          // `>=` so Last 7 days includes Weekly (7 days === 1 week).
          return relativeTimeOffset * intervalOption.duration >= opt.duration;
        }

        if (rangeStart && rangeEnd) {
          const duration = coerceEpoch(rangeEnd) - coerceEpoch(rangeStart);
          return duration >= opt.duration;
        }
      }),
    [selectedDateRange, hiddenIntervals],
  );

  const initialTimeInterval = useMemo(() => {
    if (period && intervalOptions.some((opt) => opt.key === period)) {
      const option = TIMEFRAME_INTERVAL_OPTIONS.find((intervalOption) => intervalOption.value === period);
      return {
        key: period as INTERVAL_OPTIONS,
        name: option?.label || '',
        // Use the interval option's unit duration (hour/day/week/…), not the selected
        // date-range span — otherwise switching Last 90 → Last 60 falsely clears Weekly.
        duration: option?.duration || 0,
      };
    }
    // Prefer Daily when available; otherwise first remaining option (e.g. after Hourly is hidden).
    return intervalOptions.find((opt) => opt.key === INTERVAL_OPTIONS.DAILY) || intervalOptions[0];
  }, [period, intervalOptions]);

  const [selectedTimeInterval, setSelectedTimeInterval] = useState<(typeof intervalOptions)[number] | undefined>();

  // Replace a stale interval (e.g. Hourly on Last 7/30 days) with the first valid option.
  // Skip while options are empty (transient during range sync) so we don't clear a valid period.
  useEffect(() => {
    if (!isTimePeriodValid || intervalOptions.length === 0) return;

    const currentKey = selectedTimeInterval?.key || period;
    if (!currentKey || intervalOptions.some((opt) => opt.key === currentKey)) return;

    const fallback = intervalOptions.find((opt) => opt.key === INTERVAL_OPTIONS.DAILY) || intervalOptions[0];
    if (!fallback?.key) return;

    setSelectedTimeInterval(fallback);

    const [rangeStart, rangeEnd, relativeTimeOffset, relativeTimeUnit] = selectedDateRange;
    onChange({
      start: rangeStart,
      end: rangeEnd,
      period: fallback.key,
      relative: [relativeTimeOffset as number, relativeTimeUnit as string],
    });
  }, [isTimePeriodValid, intervalOptions, selectedTimeInterval, period, selectedDateRange, onChange]);

  const handleDateRangeChange = (selectedDate: AdvancedDateRange) => {
    const [rangeStart, rangeEnd, relativeTimeOffset, relativeTimeUnit] = selectedDate;

    // `selectedTimeInterval || initialTimeInterval` can be undefined when the picked
    // range has zero duration (first-pick-of-custom echoes `[day, day]`) because
    // every interval gets filtered out — destructuring would throw and abort the
    // picker's close sequence. Default to an empty shape instead.
    const { key, duration } = selectedTimeInterval || initialTimeInterval || { key: undefined, duration: 0 };

    let nextPeriod: INTERVAL_OPTIONS | undefined = key;
    const hasRelative = relativeTimeOffset !== null && relativeTimeOffset !== undefined && !!relativeTimeUnit;

    if (hasRelative) {
      if (relativeTimeOffset === 0) {
        // Today only supports Hourly — set it immediately so Apply/Save don't keep a
        // stale Daily period and so validation stays enabled without a second click.
        nextPeriod = INTERVAL_OPTIONS.HOURLY;
        setSelectedTimeInterval({
          key: INTERVAL_OPTIONS.HOURLY,
          name: TIMEFRAME_INTERVAL_OPTIONS.find((opt) => opt.value === INTERVAL_OPTIONS.HOURLY)?.label || 'Hourly',
          duration: TIMEFRAME_INTERVAL_OPTIONS.find((opt) => opt.value === INTERVAL_OPTIONS.HOURLY)?.duration || 0,
        });
      } else {
        const unitOption =
          TIMEFRAME_INTERVAL_OPTIONS.find((opt) => opt.value === relativeTimeUnit) || TIMEFRAME_INTERVAL_OPTIONS[0];
        const rangeDuration = relativeTimeOffset * unitOption.duration;
        const isValidInterval =
          rangeDuration >= (duration || 0) &&
          !(
            key === INTERVAL_OPTIONS.HOURLY &&
            getRelativeTimeFrameDayCount(relativeTimeOffset, relativeTimeUnit) > MAX_DAYS_FOR_HOURLY_INTERVAL
          );
        if (!isValidInterval) {
          nextPeriod = undefined;
          setSelectedTimeInterval(undefined);
        }
      }
    } else if (rangeStart && rangeEnd) {
      const newDurationSeconds = coerceEpoch(rangeEnd) - coerceEpoch(rangeStart);
      const dayCount = newDurationSeconds / (24 * 60 * 60);
      const isValidInterval =
        newDurationSeconds >= (duration || 0) &&
        !(key === INTERVAL_OPTIONS.HOURLY && dayCount > MAX_DAYS_FOR_HOURLY_INTERVAL);
      if (!isValidInterval) {
        nextPeriod = undefined;
        setSelectedTimeInterval(undefined);
      }
    }

    onChange({
      start: rangeStart,
      end: rangeEnd,
      period: nextPeriod,
      relative: [relativeTimeOffset as number, relativeTimeUnit as string],
    });
  };

  const handleIntervalChange = ({ key, name, duration }: { key: INTERVAL_OPTIONS; name: string; duration: number }) => {
    setSelectedTimeInterval({
      key,
      name,
      duration,
    });

    const [rangeStart, rangeEnd, relativeTimeOffset, relativeTimeUnit] = selectedDateRange;
    // Fall back to parent relative so changing interval never drops preset metadata.
    const nextRelativeOffset = relativeTimeOffset ?? relative?.[0];
    const nextRelativeUnit = relativeTimeUnit ?? relative?.[1];

    onChange({
      start: rangeStart,
      end: rangeEnd,
      period: key,
      relative: [nextRelativeOffset as number, nextRelativeUnit as string],
    });
  };

  const showPreview = () => {
    const dateRange = selectedDateRange;
    const [rangeStart, rangeEnd, relativeTimeOffset] = dateRange;

    const shouldShowPreview = rangeStart && rangeEnd && relativeTimeOffset !== null && relativeTimeOffset !== undefined;

    return shouldShowPreview ? (
      <div className={CLASSES.PREVIEW}>
        {relativeTimeOffset === 0
          ? `${epochToDateTimeString(new Date().getTime(), 'en-US', DATE_FORMAT_OPTIONS)}`
          : `${epochToDateTimeString(rangeStart, 'en-US', DATE_FORMAT_OPTIONS)} - ${epochToDateTimeString(
              rangeEnd,
              'en-US',
              DATE_FORMAT_OPTIONS,
            )}`}
      </div>
    ) : null;
  };

  useEffect(() => {
    const timeInterval = selectedTimeInterval || initialTimeInterval;
    const isTimeIntervalValid = !!timeInterval?.key;

    const isValid = isTimePeriodValid && isTimeIntervalValid;
    onValidation(isValid);
  }, [isTimePeriodValid, selectedTimeInterval, initialTimeInterval, onValidation]);

  return (
    <section className={MAIN_CLASS}>
      <label className={CLASSES.LABEL} htmlFor="select-period">
        Select a period of time
      </label>
      <div className={CLASSES.BLOCK}>
        <DateRangePicker
          className={`${CLASSES.DATE_RANGE} half`}
          value={selectedDateRange}
          onChange={handleDateRangeChange}
          maxDate={new Date()}
          presets={DATERANGE_PRESETS}
          ariaLabel="Select a time frame"
          id="select-period"
        />
        {showPreview()}
      </div>
      <label className={CLASSES.LABEL} htmlFor="select-interval">
        Select an interval
      </label>
      <FlightSelect
        className={`${CLASSES.DATE_RANGE_DD}`}
        options={intervalOptions}
        selected={selectedTimeInterval || initialTimeInterval}
        ariaControlsId={'select-interval'}
        width={'100%'}
        handleOptionClick={handleIntervalChange}
      />
    </section>
  );
}
